/*
 * <card-purchasability> — hides a product card's purchase icon when the card's variant is
 * "Udsolgt online" (ClickUp 869f9nc39).
 *
 * The product page already stands its buy buttons down for these variants
 * (<purchasability-gate> in store-locator.js, ClickUp 869eq7r46 / 869f86cbr), but a card's
 * quick-add icon stayed live and opened a drawer whose button was then greyed out. Shopify
 * still counts the variant as available — the shop stock is in the total — so Liquid cannot
 * decide it: the verdict needs per-location quantities, which only the Storefront API carries.
 *
 * So the card renders this data-only element (snippets/_card-purchasability.liquid, and
 * quickAdd() in relewise-cards.js for the client-side collection cards) with the Liquid half
 * of the inputs — the inventory policy — and this script fetches the stock half (the
 * variant's Centrallager flag, its sellable figure and the per-shop stock), batched: one
 * Storefront request for every card that connects within a short window (BATCH_WINDOW_MS), up
 * to BATCH_SIZE per request.
 * Answers are kept for the page view, so cards that re-render (the client cards when their
 * background data lands, Relewise on every filter or sort) re-apply from the cache with no
 * request at all.
 *
 * Fails open, exactly like the product page gate: no token, a network error or a variant the
 * API does not return leaves the icon where it is. Someone may then open a drawer whose button
 * is greyed — today's behaviour — whereas hiding the icon on a healthy product is lost revenue
 * nobody reports.
 *
 * A standalone file, not part of store-locator.js: that script is 84KB and loads on the product
 * page only, and loading it site-wide was rejected in 869eq7r46. The cost is a second copy of
 * the sold-out rule (isSoldOutOnline below), which qa/sold-out-online/parity.test.cjs holds to
 * store-locator.js's isVariantDiscontinued. Change one, run the test.
 *
 * Page config rides on the script tag in layout/theme.liquid: data-shop-domain,
 * data-storefront-token, data-warehouse-ax-id.
 */
(function (root) {
  'use strict';

  // Same version string as StorefrontClient in store-locator.js, so the card and the product
  // page read stock through one API version by construction. Change both together.
  const API_VERSION = '2025-01';
  const BATCH_SIZE = 25;
  // How long a batch stays open for more cards. Not 0: on the per-card fetch paths (the Liquid
  // card via ?view=relewise-product-card*, the recommendation rows) each card lands in its own
  // task as its fetch returns, spread over ~1s — measured on /collections/krukker, a zero-delay
  // flush sent one request per card (8 for 8). The window slides with each arrival, capped so
  // the first card in a batch never waits longer than BATCH_MAX_WAIT_MS.
  const BATCH_WINDOW_MS = 100;
  const BATCH_MAX_WAIT_MS = 300;

  // VARIANTS_AVAILABILITY_QUERY in store-locator.js, field for field and directive for
  // directive. first: 100 on both connections because the warehouse verdict compares against
  // the best-stocked shop, so every pickup row has to arrive (40 shops today). The bare
  // @inContext reads quantityAvailable in the shop's primary market, the figure the verdict
  // needs (869fb4a09, see deliveryWarehouseInStock) — drop it from one copy and the card and
  // the product page disagree. centrallagerInStock is the warehouse's own yes/no, read first
  // (869fbkn3h round 2); null until the definition's Storefront access is Read.
  // qa/sold-out-online/parity.test.cjs checks both, in all three queries.
  const QUERY = `
    query CardPurchasability($ids: [ID!]!) @inContext {
      nodes(ids: $ids) {
        ... on ProductVariant {
          id
          centrallagerInStock: metafield(namespace: "dtails", key: "centrallager_in_stock") { value }
          quantityAvailable
          storeAvailability(first: 100) {
            nodes {
              quantityAvailable
              location { id }
            }
          }
        }
      }
      locations(first: 100) {
        nodes {
          id
          axLocationId: metafield(namespace: "ax_ns", key: "location_id") { value }
        }
      }
    }
  `;

  /**
   * The "Udsolgt online" verdict — what isVariantDiscontinued() in store-locator.js reaches
   * through the delivery table, reduced to the inputs that can change it (ClickUp 869fbkn3h).
   * Factory date and lead time are not among them: with an empty warehouse and overselling on,
   * the table ships from the arrival date, on the lead time, or shows the fallback card — never
   * sold out — and with overselling off it is sold out whatever they say.
   *
   * - Warehouse in stock → buyable. Same test as deliveryWarehouseInStock: the Centrallager flag
   *   says yes and the sellable quantity is above 0, or else the sellable quantity beats every
   *   shop. A false or missing flag never overrules the comparison (869fbkn3h round 2).
   * - Otherwise overselling off (deny) → sold out online.
   * - Otherwise buyable, however much stock stands in the shops.
   */
  function isSoldOutOnline({ inventoryPolicy, largestSourceQuantity, storeQuantities, centrallagerInStock = null }) {
    if (centrallagerInStock === true && largestSourceQuantity > 0) return false;
    const largestShop = storeQuantities.reduce((max, qty) => Math.max(max, qty), 0);
    if (largestSourceQuantity > largestShop) return false;
    return inventoryPolicy === 'deny';
  }

  /**
   * The Centrallager flag as the Storefront API returns it — a copy of store-locator.js's
   * deliveryParseWarehouseFlag, held equal by the parity test. { value: "true" } → true,
   * { value: "false" } → false, anything else (access off, no node, an unexpected value) → null.
   * Strict string compare: Boolean("false") is true.
   */
  function parseWarehouseFlag(metafield) {
    const value = metafield?.value;
    if (value === 'true') return true;
    if (value === 'false') return false;
    return null;
  }

  /**
   * Shop-floor quantities for one variant node, the warehouse's own location excluded — the
   * same filter as fetchVariantsAvailability() in store-locator.js. Excluded only when an AX id
   * is configured: without that guard every location missing an AX id would match an empty
   * one and drop out, and nothing would ever read as sold out.
   */
  function shopQuantities(node, axLocationById, warehouseAxId) {
    return (node.storeAvailability?.nodes || [])
      .filter((row) => !warehouseAxId || (axLocationById.get(row.location?.id) || null) !== warehouseAxId)
      .map((row) => row.quantityAvailable || 0);
  }

  const api = { isSoldOutOnline, parseWarehouseFlag, shopQuantities };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof document === 'undefined' || !root.customElements || root.customElements.get('card-purchasability')) return;

  const config = { ...(document.currentScript?.dataset || {}) };

  // gid → Promise<stock|null>, shared by every card asking about that variant.
  const pendingByVariant = new Map();
  // gid → stock|null once answered, so a re-rendered card hides its icon synchronously,
  // before paint, instead of flashing it for a microtask.
  const settledByVariant = new Map();
  let queue = [];
  let flushTimer = 0;
  let batchOpenedAt = 0;

  function requestStock(gid) {
    if (!pendingByVariant.has(gid)) {
      let resolve;
      pendingByVariant.set(gid, new Promise((r) => { resolve = r; }));
      queue.push({ gid, resolve });
      scheduleFlush();
    }
    return pendingByVariant.get(gid);
  }

  // A grid that connects in one task (the deferred script upgrading the page, or one innerHTML
  // write) lands in one batch either way; the sliding window is for cards that trickle in.
  function scheduleFlush() {
    const now = Date.now();
    if (!flushTimer) batchOpenedAt = now;
    clearTimeout(flushTimer);
    const wait = Math.min(BATCH_WINDOW_MS, Math.max(0, batchOpenedAt + BATCH_MAX_WAIT_MS - now));
    flushTimer = setTimeout(flush, wait);
  }

  function flush() {
    flushTimer = 0;
    const batch = queue;
    queue = [];
    for (let i = 0; i < batch.length; i += BATCH_SIZE) fetchBatch(batch.slice(i, i + BATCH_SIZE));
  }

  async function fetchBatch(entries) {
    let stockById = null;
    try {
      const res = await fetch(`https://${config.shopDomain}/api/${API_VERSION}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': config.storefrontToken,
        },
        body: JSON.stringify({ query: QUERY, variables: { ids: entries.map((e) => e.gid) } }),
      });
      if (!res.ok) throw new Error(`Storefront API ${res.status}`);
      const json = await res.json();
      if (json.errors) throw new Error(json.errors.map((e) => e.message).join(', '));
      const axLocationById = new Map(
        (json.data?.locations?.nodes || []).map((loc) => [loc.id, loc.axLocationId?.value || null])
      );
      stockById = new Map();
      (json.data?.nodes || []).forEach((node) => {
        if (!node?.id) return;
        stockById.set(node.id, {
          // `?? 0` as both store-locator.js paths read it, so a null figure means what it
          // means on the product page.
          quantityAvailable: node.quantityAvailable ?? 0,
          shopQuantities: shopQuantities(node, axLocationById, config.warehouseAxId || ''),
          centrallagerInStock: parseWarehouseFlag(node.centrallagerInStock),
        });
      });
    } catch {
      stockById = null;
    }

    entries.forEach(({ gid, resolve }) => {
      if (stockById) {
        // A variant the API did not return is cached as "no answer" — asking again won't help.
        settledByVariant.set(gid, stockById.get(gid) || null);
      } else {
        // A failed request is not cached: the next card that asks for the variant retries it.
        pendingByVariant.delete(gid);
      }
      resolve(stockById ? stockById.get(gid) || null : null);
    });
  }

  class CardPurchasability extends HTMLElement {
    connectedCallback() {
      const { variantId } = this.dataset;
      if (!variantId || !config.shopDomain || !config.storefrontToken) return;
      const gid = `gid://shopify/ProductVariant/${variantId}`;
      if (settledByVariant.has(gid)) {
        this.apply(settledByVariant.get(gid));
        return;
      }
      requestStock(gid).then((stock) => {
        if (this.isConnected) this.apply(stock);
      });
    }

    apply(stock) {
      if (!stock) return;
      const soldOut = isSoldOutOnline({
        inventoryPolicy: this.dataset.policy,
        largestSourceQuantity: stock.quantityAvailable,
        storeQuantities: stock.shopQuantities,
        centrallagerInStock: stock.centrallagerInStock,
      });
      const selector = this.dataset.target;
      if (!soldOut || !selector) return;
      // `.card` is the root of both card types (<product-card class="card …"> and the small
      // card's `.card.card--related`), so the target never reaches a neighbouring card.
      const card = this.closest('.card') || this.parentElement;
      card?.querySelectorAll(selector).forEach((el) => {
        el.hidden = true;
      });
    }
  }

  root.customElements.define('card-purchasability', CardPurchasability);
})(typeof window === 'undefined' ? globalThis : window);
